import { useState } from "react";
import { config } from "../../../../../utils/config";
import {
  usePaymentRequestData,
  useRequestPaymentMutation,
} from "./use-payment-request-queries";
import { PaymentRequestResult } from "../types";

interface PaymentRequestToastState {
  open: boolean;
  message: string;
  variant: "default" | "destructive" | "success";
}

type ToastConfig = Pick<PaymentRequestToastState, "message" | "variant">;

const getPaymentResultToast = (
  result: PaymentRequestResult,
  ops: string
): ToastConfig => {
  const { quantidadeTotal, quantidadeSucesso, quantidadeFalha } = result;

  if (quantidadeTotal === 0) {
    return {
      message: `Nenhum pagamento pendente encontrado para ${ops} na data selecionada.`,
      variant: "default",
    };
  }

  if (quantidadeFalha === 0) {
    return {
      message: `Pagamentos para ${ops} processados com sucesso! ${quantidadeSucesso} de ${quantidadeTotal} pagamentos enviados.`,
      variant: "success",
    };
  }

  if (quantidadeSucesso === 0) {
    return {
      message: `Falha ao processar pagamentos para ${ops}. ${quantidadeFalha} de ${quantidadeTotal} pagamentos falharam.`,
      variant: "destructive",
    };
  }

  return {
    message: `Processamento parcial para ${ops}: ${quantidadeSucesso} sucessos e ${quantidadeFalha} falhas de ${quantidadeTotal} pagamentos.`,
    variant: "default",
  };
};

export function usePaymentRequest() {
  const selectedFund = `xama_${config.stage}`;
  const [toast, setToast] = useState<PaymentRequestToastState>({
    open: false,
    message: "",
    variant: "default",
  });

  const {
    data: paymentRequestData,
    isLoading,
    refetch,
  } = usePaymentRequestData(selectedFund);
  const requestPaymentMutation = useRequestPaymentMutation();

  const showToast = (config: ToastConfig) => {
    setToast({ open: true, ...config });
  };



  const handleRequestPayment = async (ops: string, dateToCheckout: string) => {
    if (!selectedFund) return;

    try {
      const result = await requestPaymentMutation.mutateAsync({
        dbName: selectedFund,
        ops,
        dateToCheckout,
      });

      if (!result) {
        showToast({
          message: "Erro ao solicitar pagamentos - resposta inválida",
          variant: "destructive",
        });
        return;
      }

      const toastConfig = getPaymentResultToast(result, ops);
      showToast(toastConfig);

      await refetch();
    } catch (error) {
      showToast({
        message: "Erro ao solicitar pagamento. Por favor, tente novamente.",
        variant: "destructive",
      });
    }
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  return {
    selectedFund,
    paymentRequestData,
    isLoading: isLoading || requestPaymentMutation.isPending,
    toast,

    handleRequestPayment,
    closeToast,
    refetch,
  };
}
